import React, { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import axios from 'axios';
import { useSelector } from 'react-redux';
import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper,
  Snackbar, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import { apiHost } from '../../utils/apiHost';

// Stok opname: hitung stok fisik, selisih terhadap stok sistem dikoreksi dengan alasan, tercatat di riwayat stok.
// Server: controller/stokOpnameController.js (GET /stok-opname/produk, POST /stok-opname, GET /stok-opname[/:uuid]).

const base = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  return `${protocol}://${apiHost().replace(/^https?:\/\//, '')}`;
};
const ambil = (url) => axios.get(url, { withCredentials: true }).then((r) => r.data);
const rupiah = (v) => `Rp ${Math.round(Math.abs(Number(v) || 0)).toLocaleString('id-ID')}`;
const bertanda = (v) => `${v < 0 ? '−' : v > 0 ? '+' : ''}${rupiah(v)}`;
const tanggal = (iso) => (iso ? new Date(iso).toLocaleString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-');
const tgl = (d) => { const p = (x) => String(x).padStart(2, '0'); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`; };
const idBaru = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `op-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);

const warnaSelisih = (s) => (s < 0 ? 'error.main' : s > 0 ? 'warning.dark' : 'text.secondary');
const tandaUnit = (s) => (s > 0 ? `+${s}` : `${s}`);

const HitungStok = ({ cabangPilih, onSimpan }) => {
  const [cari, setCari] = useState('');
  const [isi, setIsi] = useState({}); // baranguuid -> { fisik: '', alasan: '', catatan: '' }
  const [konfirmasi, setKonfirmasi] = useState(false);
  const [catatanUmum, setCatatanUmum] = useState('');
  const [proses, setProses] = useState(false);
  const [galat, setGalat] = useState('');
  const [clientId, setClientId] = useState(idBaru);

  const kunci = cabangPilih === null ? null : `${base()}/stok-opname/produk${cabangPilih ? `?cabang=${cabangPilih}` : ''}`;
  const { data, error, isLoading, mutate } = useSWR(kunci, ambil, { revalidateOnFocus: false });
  const produk = useMemo(() => (Array.isArray(data?.data) ? data.data : []), [data]);
  const alasan = Array.isArray(data?.alasan) ? data.alasan : [];

  useEffect(() => { setIsi({}); setGalat(''); }, [cabangPilih]);

  const tampil = useMemo(() => {
    const q = cari.trim().toLowerCase();
    return q ? produk.filter((p) => p.namabarang.toLowerCase().includes(q) || (p.kodebarang || '').toLowerCase().includes(q)) : produk;
  }, [produk, cari]);

  const ubah = (id, k, v) => setIsi((s) => ({ ...s, [id]: { ...(s[id] || {}), [k]: v } }));
  const baris = (p) => {
    const x = isi[p.baranguuid] || {};
    const sudah = x.fisik !== undefined && x.fisik !== '';
    const fisik = sudah ? Number(x.fisik) : null;
    const valid = sudah && Number.isInteger(fisik) && fisik >= 0;
    const selisih = valid ? fisik - p.stok : 0;
    return { x, sudah, fisik, valid, selisih };
  };

  const dihitung = produk.map((p) => ({ p, ...baris(p) })).filter((b) => b.sudah);
  const tidakValid = dihitung.filter((b) => !b.valid);
  const berselisih = dihitung.filter((b) => b.valid && b.selisih !== 0);
  const kurangInfo = berselisih.filter((b) => !b.x.alasan || (b.x.alasan === 'lainnya' && (b.x.catatan || '').trim().length < 3));
  const nilai = berselisih.reduce((a, b) => a + b.selisih * (b.p.hargaModal || 0), 0);
  const siap = dihitung.length > 0 && tidakValid.length === 0 && kurangInfo.length === 0;

  const simpan = async () => {
    setProses(true); setGalat('');
    try {
      const res = await axios.post(`${base()}/stok-opname`, {
        cabang: cabangPilih || undefined,
        clientId,
        catatan: catatanUmum.trim() || undefined,
        items: dihitung.map((b) => ({
          baranguuid: b.p.baranguuid,
          stokFisik: b.fisik,
          alasan: b.selisih !== 0 ? b.x.alasan : undefined,
          catatan: (b.x.catatan || '').trim() || undefined,
        })),
      }, { withCredentials: true });
      setKonfirmasi(false); setIsi({}); setCatatanUmum(''); setClientId(idBaru());
      onSimpan(res.data.message || 'Opname tersimpan');
      mutate();
    } catch (e) {
      setKonfirmasi(false);
      setGalat(e?.response?.data?.message || e?.message || 'Gagal menyimpan opname');
    } finally {
      setProses(false);
    }
  };

  if (kunci === null) return <Alert severity="info">Pilih cabang terlebih dahulu.</Alert>;
  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Isi <b>stok fisik</b> hanya untuk produk yang Anda hitung (kosongkan yang lain). Produk yang berbeda dari stok sistem wajib diberi alasan.
        Selisih akhir dihitung ulang server dari stok pada saat disimpan, jadi penjualan yang terjadi saat Anda menghitung tidak ikut salah.
      </Alert>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error?.response?.data?.message || 'Gagal memuat produk'}</Alert>}
      {galat && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setGalat('')}>{galat}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField size="small" label="Cari produk / kode" value={cari} onChange={(e) => setCari(e.target.value)} sx={{ minWidth: 260 }} />
        <Typography variant="body2" color="text.secondary">
          {dihitung.length} dihitung · {berselisih.length} berselisih · perkiraan nilai <b style={{ color: nilai < 0 ? '#d32f2f' : undefined }}>{bertanda(nilai)}</b>
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" disabled={!dihitung.length} onClick={() => setIsi({})}>Kosongkan</Button>
        <Button variant="contained" disabled={!siap || proses} onClick={() => setKonfirmasi(true)}>Simpan opname</Button>
      </Box>
      {dihitung.length > 0 && !siap && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {tidakValid.length > 0 ? 'Stok fisik harus bilangan bulat 0 atau lebih. ' : ''}
          {kurangInfo.length > 0 ? `${kurangInfo.length} produk berselisih belum ada alasan (atau catatan untuk alasan Lainnya).` : ''}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Produk</TableCell>
              <TableCell align="right">Stok sistem</TableCell>
              <TableCell sx={{ width: 130 }}>Stok fisik</TableCell>
              <TableCell align="right">Selisih</TableCell>
              <TableCell sx={{ width: 220 }}>Alasan</TableCell>
              <TableCell sx={{ width: 220 }}>Catatan</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && <TableRow><TableCell colSpan={6}>Memuat…</TableCell></TableRow>}
            {!isLoading && tampil.length === 0 && <TableRow><TableCell colSpan={6}>Tidak ada produk.</TableCell></TableRow>}
            {tampil.map((p) => {
              const b = baris(p);
              return (
                <TableRow key={p.baranguuid} hover selected={b.sudah}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{p.namabarang}</Typography>
                    <Typography variant="caption" color="text.secondary">{[p.kodebarang, p.kategori].filter(Boolean).join(' · ')}</Typography>
                  </TableCell>
                  <TableCell align="right">{p.stok}</TableCell>
                  <TableCell>
                    {p.ikutBahan ? (
                      <Typography variant="caption" color="text.secondary">Ikut bahan baku (hitung di Bahan Baku)</Typography>
                    ) : (
                      <TextField
                        size="small" type="number" value={b.x.fisik ?? ''} error={b.sudah && !b.valid}
                        inputProps={{ min: 0, step: 1, 'aria-label': `Stok fisik ${p.namabarang}` }}
                        onChange={(e) => ubah(p.baranguuid, 'fisik', e.target.value)}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ color: warnaSelisih(b.selisih), fontWeight: 700 }}>{b.valid ? (b.selisih === 0 ? 'Pas' : tandaUnit(b.selisih)) : ''}</TableCell>
                  <TableCell>
                    {b.valid && b.selisih !== 0 && (
                      <TextField select size="small" fullWidth value={b.x.alasan || ''} error={!b.x.alasan} onChange={(e) => ubah(p.baranguuid, 'alasan', e.target.value)} label="Alasan">
                        {alasan.map((a) => <MenuItem key={a.key} value={a.key}>{a.label}</MenuItem>)}
                      </TextField>
                    )}
                  </TableCell>
                  <TableCell>
                    {b.sudah && (
                      <TextField
                        size="small" fullWidth value={b.x.catatan || ''} inputProps={{ maxLength: 200 }}
                        error={b.x.alasan === 'lainnya' && (b.x.catatan || '').trim().length < 3}
                        onChange={(e) => ubah(p.baranguuid, 'catatan', e.target.value)} placeholder="Opsional"
                      />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={konfirmasi} onClose={() => !proses && setKonfirmasi(false)} fullWidth maxWidth="sm">
        <DialogTitle>Simpan opname?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {dihitung.length} produk dihitung, {berselisih.length} akan disesuaikan. Stok sistem diganti menjadi stok fisik dan tercatat di Riwayat Produk. Tindakan ini tidak bisa dibatalkan (koreksi dengan opname baru).
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>Perkiraan nilai selisih (× harga modal): <b>{bertanda(nilai)}</b></Typography>
          <TextField size="small" fullWidth label="Catatan opname (opsional)" value={catatanUmum} inputProps={{ maxLength: 255 }} onChange={(e) => setCatatanUmum(e.target.value)} placeholder="mis. hitung akhir bulan" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKonfirmasi(false)} disabled={proses}>Batal</Button>
          <Button variant="contained" onClick={simpan} disabled={proses}>{proses ? 'Menyimpan…' : 'Ya, simpan'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const Riwayat = ({ cabangPilih, superadmin }) => {
  const [dari, setDari] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 29); return tgl(d); });
  const [sampai, setSampai] = useState(() => tgl(new Date()));
  const [detail, setDetail] = useState(null);
  const query = new URLSearchParams({ dari, sampai, ...(cabangPilih && { cabang: cabangPilih }) }).toString();
  const { data, error, isLoading } = useSWR(`${base()}/stok-opname?${query}`, ambil, { revalidateOnFocus: false });
  const list = Array.isArray(data?.data) ? data.data : [];
  const r = data?.ringkasan || {};

  const buka = async (o) => {
    setDetail({ ...o, items: null });
    try {
      const res = await axios.get(`${base()}/stok-opname/${o.uuid}`, { withCredentials: true });
      setDetail(res.data.data);
    } catch (e) {
      setDetail({ ...o, items: [], galat: e?.response?.data?.message || 'Gagal memuat rincian' });
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <TextField size="small" type="date" label="Dari" InputLabelProps={{ shrink: true }} value={dari} onChange={(e) => setDari(e.target.value)} />
        <TextField size="small" type="date" label="Sampai" InputLabelProps={{ shrink: true }} value={sampai} onChange={(e) => setSampai(e.target.value)} />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(3, 1fr)' }, gap: 1.5, mb: 2 }}>
        {[
          ['Jumlah opname', r.jumlah ?? 0, 'text.primary'],
          ['Produk berselisih', r.produkBerselisih ?? 0, 'warning.dark'],
          ['Nilai selisih', bertanda(r.nilaiSelisih || 0), (r.nilaiSelisih || 0) < 0 ? 'error.main' : 'text.primary'],
        ].map(([judul, nilai, warna]) => (
          <Card key={judul} variant="outlined">
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">{judul}</Typography>
              <Typography variant="h6" color={warna} fontWeight={700}>{nilai}</Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error?.response?.data?.message || 'Gagal memuat riwayat opname'}</Alert>}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Waktu</TableCell>
              {superadmin && <TableCell>Cabang</TableCell>}
              <TableCell>Petugas</TableCell>
              <TableCell align="right">Dihitung</TableCell>
              <TableCell align="right">Berselisih</TableCell>
              <TableCell align="right">Unit +/−</TableCell>
              <TableCell align="right">Nilai selisih</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && <TableRow><TableCell colSpan={8}>Memuat…</TableCell></TableRow>}
            {!isLoading && list.length === 0 && <TableRow><TableCell colSpan={8}>Belum ada opname pada rentang ini.</TableCell></TableRow>}
            {list.map((o) => (
              <TableRow key={o.uuid} hover>
                <TableCell>{tanggal(o.createdAt)}{o.catatan && <Typography variant="caption" color="text.secondary" display="block">{o.catatan}</Typography>}</TableCell>
                {superadmin && <TableCell>{o.namacabang || '-'}</TableCell>}
                <TableCell>{o.petugas || '-'}</TableCell>
                <TableCell align="right">{o.jumlahItem}</TableCell>
                <TableCell align="right">{o.jumlahBerselisih}</TableCell>
                <TableCell align="right">+{o.totalTambah} / −{o.totalKurang}</TableCell>
                <TableCell align="right" sx={{ color: o.nilaiSelisih < 0 ? 'error.main' : undefined, fontWeight: 700 }}>{bertanda(o.nilaiSelisih)}</TableCell>
                <TableCell align="right"><Button size="small" onClick={() => buka(o)}>Rincian</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} fullWidth maxWidth="md">
        <DialogTitle>Rincian opname · {detail ? tanggal(detail.createdAt) : ''}</DialogTitle>
        <DialogContent>
          {detail?.galat && <Alert severity="error" sx={{ mb: 1 }}>{detail.galat}</Alert>}
          {detail && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {[detail.namacabang, detail.petugas && `oleh ${detail.petugas}`, detail.catatan].filter(Boolean).join(' · ')}
            </Typography>
          )}
          {detail?.items === null ? <Typography>Memuat…</Typography> : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Produk</TableCell>
                    <TableCell align="right">Sistem</TableCell>
                    <TableCell align="right">Fisik</TableCell>
                    <TableCell align="right">Selisih</TableCell>
                    <TableCell>Alasan</TableCell>
                    <TableCell align="right">Nilai</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(detail?.items || []).map((i) => (
                    <TableRow key={i.baranguuid}>
                      <TableCell>{i.namabarang}{i.kodebarang && <Typography variant="caption" color="text.secondary" display="block">{i.kodebarang}</Typography>}</TableCell>
                      <TableCell align="right">{i.stokSistem}</TableCell>
                      <TableCell align="right">{i.stokFisik}</TableCell>
                      <TableCell align="right" sx={{ color: warnaSelisih(i.selisih), fontWeight: 700 }}>{i.selisih === 0 ? 'Pas' : tandaUnit(i.selisih)}</TableCell>
                      <TableCell>{i.alasanLabel ? <Chip size="small" label={i.alasanLabel} /> : '-'}{i.catatan && <Typography variant="caption" color="text.secondary" display="block">{i.catatan}</Typography>}</TableCell>
                      <TableCell align="right">{i.selisih === 0 ? '-' : bertanda(i.nilai)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDetail(null)}>Tutup</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

const StokOpname = () => {
  const { user } = useSelector((state) => state.auth);
  const superadmin = user?.role === 'superadmin';
  const [tab, setTab] = useState(0);
  const [cabang, setCabang] = useState('');
  const [notif, setNotif] = useState(null);
  const { data: cabangData } = useSWR(superadmin ? `${base()}/cabangbyrole?bentuk=objek` : null, ambil);
  const daftarCabang = Array.isArray(cabangData?.data) ? cabangData.data : [];

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6">Stok Opname</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Hitung stok fisik di rak/gudang, lalu samakan dengan stok sistem lengkap dengan alasan selisihnya (rusak, hilang, kedaluwarsa, dll.).
      </Typography>
      {superadmin && (
        <TextField size="small" select label="Cabang" value={cabang} onChange={(e) => setCabang(e.target.value)} sx={{ minWidth: 220, mb: 2 }}>
          {tab === 1 && <MenuItem value="">Semua cabang</MenuItem>}
          {daftarCabang.map((c) => <MenuItem key={c.uuid} value={c.uuid}>{c.namacabang}</MenuItem>)}
        </TextField>
      )}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Hitung stok" />
        <Tab label="Riwayat opname" />
      </Tabs>
      {tab === 0
        ? <HitungStok cabangPilih={superadmin ? (cabang || null) : ''} onSimpan={(text) => setNotif({ severity: 'success', text })} />
        : <Riwayat cabangPilih={superadmin ? cabang : ''} superadmin={superadmin} />}
      <Snackbar open={Boolean(notif)} autoHideDuration={5000} onClose={() => setNotif(null)}>
        {notif ? <Alert severity={notif.severity} onClose={() => setNotif(null)}>{notif.text}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default StokOpname;
